import moment from 'moment';
import fb from '@/firebaseConfig';
import { sortByTitle } from '@/helpers/contentHelpers';

/*

  STATE

 */

const state = {
  defaultStacks: {},
  defaultStackIds: [],
  userStacks: {},
  isLoaded: false,
};

/*

  GETTERS

 */

const getters = {
  allStacks: state => {
    const { defaultStacks, userStacks } = state;
    const stacks = {};
    Object.keys(defaultStacks).forEach(id => {
      stacks[id] = defaultStacks[id];
    });
    Object.keys(userStacks).forEach(id => {
      stacks[id] = userStacks[id];
    });
    return stacks;
  },

  // If a user has edited a default stack,
  // don't show stack in Default list
  filteredDefaultStackIds: state => {
    const { defaultStackIds, userStacks } = state;
    return defaultStackIds.filter(id => !userStacks[id]);
  },

  sortedUserStackIds: (state, getters) => {
    const { userStacks } = state;
    const { allStacks } = getters;
    const unsortedIds = Object.keys(userStacks);
    return sortByTitle(allStacks, unsortedIds);
  },
};

/*

  ACTIONS

 */

const actions = {
  init({ commit, dispatch }, userStacks) {
    const { isLoaded } = this;
    if (userStacks) {
      commit('setUserStacks', userStacks);
    }
    if (!isLoaded) {
      dispatch('getDefaultStacks');
    }
  },

  getDefaultStacks({ commit }) {
    fb.stacksCollection
      .orderBy('title')
      .get()
      .then(querySnapshot => {
        const stacksData = {};
        const stacksIds = [];
        querySnapshot.forEach(doc => {
          const { id } = doc;
          const data = doc.data();
          stacksData[id] = data;
          stacksData[id].source = 'default';
          stacksIds.push(id);
        });
        commit('setDefaultStacks', { stacksData, stacksIds });
      })
      .catch(error => {
        console.log('Error getting audio', error);
      });
  },

  addCardToStack({ dispatch, rootState, getters }, stackId) {
    const cardId = rootState.ui.currentId;
    const stack = getters.allStacks[stackId];
    const updatedStack = { ...stack };
    updatedStack.cardIds = [...stack.cardIds, cardId];
    dispatch('updateStack', updatedStack);
  },

  updateStack({ commit, dispatch }, data) {
    const stack = { ...data };
    const isNewStack = !stack.id;
    if (isNewStack) {
      stack.id = `stack-${moment().format('YYYYMMDDHHmmssS')}`;
    }
    if (stack.title === ' ') {
      stack.title = '';
    }
    stack.lastUpdatedAt = moment().format();
    commit('updateStack', stack);
    dispatch('saveStackToDB', stack);
    if (isNewStack) {
      dispatch('analytics/createStack', stack.id, { root: true });
    } else {
      dispatch('analytics/editStack', stack.id, { root: true });
    }
  },

  saveStackToDB({ rootState }, stack) {
    const userId = rootState.user.uid;
    const stackId = stack.id;
    fb.usersCollection
      .doc(userId)
      .update({ [`stacks.${stackId}`]: stack })
      .catch(error => {
        console.log('Error updating stack', error);
      });
  },

  deleteStack({ commit, dispatch, rootState }, stackId) {
    const userId = rootState.user.uid;
    if (stackId) {
      commit('removeStack', stackId);
      fb.usersCollection
        .doc(userId)
        .update({ [`stacks.${stackId}`]: fb.dbFieldValue.delete() })
        .catch(error => {
          console.log('Error deleting stack', error);
        });
      dispatch('analytics/deleteStack', stackId, { root: true });
    }
  },
};

/*

  MUTATIONS

 */

const mutations = {
  setUserStacks(state, data) {
    const userStacks = { ...data };
    Object.keys(userStacks).forEach(id => {
      userStacks[id].source = 'user';
    });
    state.userStacks = userStacks;
  },

  setDefaultStacks(state, data) {
    const { stacksData, stacksIds } = data;
    state.defaultStacks = stacksData;
    state.defaultStackIds = stacksIds;
    state.isLoaded = true;
  },

  // Any stack a user updates becomes a user stack
  updateStack(state, stack) {
    const { id } = stack;
    const userStacks = { ...state.userStacks };
    userStacks[id] = { ...stack, source: 'user' };
    state.userStacks = userStacks;
  },

  removeStack(state, stackId) {
    const userStacks = { ...state.userStacks };
    delete userStacks[stackId];
    state.userStacks = userStacks;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
